import { provideEffects } from '@ngrx/effects';

import { ExportDataService } from '../services/file/export-data.service';
import { authGuard } from '../services/guards/auth.guard';
import { permissionGuard } from '../services/guards/permission.guard';
import { PermissionValue, ResourceValue } from '../services/permissions/permissions.types';
import { ListingPageService } from '../shared/components/listing-page/listing-page.service';
import { QueryParamsHandlerService } from '../shared/components/listing-page/services/query-params-handler/query-params-handler.service';
import { ExportFileUrl } from '../shared/constants/export.constants';
import { EXPORT_URL_TOKEN } from '../shared/constants/tokens';
import { PortalPath, PortalRoute, type PortalRouteData, RouteItem } from './app.types';
import { TitleTranslateResolver } from '../services/translate/title-translate.resolver';
import {
  devicesDCUListingModelProvider,
  devicesMeteringListingModelProvider,
  devicesSensorsListingModelProvider
} from './app.providers';
import { TranslateRegistry } from '../services/translate/translate.registry';
import * as DevicesListingPageEffects from '../pages/equipment/devices/services/devices-listing-page.effects';
import { devicesMapConfigProvider } from '../pages/equipment/devices/services/devices-map-config.service';
import { MeteringListingFiltersSourceService } from '../pages/equipment/devices/services/filter-sources/metering-listing-filters-source.service';
import { DcuListingFiltersSourceService } from '../pages/equipment/devices/services/filter-sources/dcu-listing-filters-source.service';
import { SensorsListingFiltersSourceService } from '../pages/equipment/devices/services/filter-sources/sensors-listing-filters-source.service';

export const devicesRoutes: PortalRoute = {
  path: PortalPath.devices,
  canMatch: [authGuard, permissionGuard],
  data: {
    routeItem: new RouteItem({
      group: TranslateRegistry.get.MAIN_MENU.SECTION.EQUIPMENT,
      title: TranslateRegistry.get.MAIN_MENU.TITLE.DEVICES,
      hasRight: {
        resourceValue: ResourceValue.DEVICES,
        targetPermissionValue: PermissionValue.VIEW
      },
      icon: 'devices',
      path: PortalPath.devices
    })
  } satisfies PortalRouteData,
  children: [
    {
      path: '',
      title: TitleTranslateResolver,
      data: {
        hasListingLayout: true,
        routeItem: null,
        titleTranslation: TranslateRegistry.get.MAIN_MENU.TITLE.DEVICES
      },
      loadComponent: () =>
        import('../pages/equipment/devices/components/devices.component').then(c => c.DevicesComponent),
      providers: [devicesMapConfigProvider]
    },
    {
      path: `${PortalPath.dcu}/:id`,
      title: TitleTranslateResolver,
      canMatch: [permissionGuard],
      data: {
        hasDeviceDetailsLayout: true,
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.DCUS_BASE_STATIONS,
          hasRight: {
            resourceValue: ResourceValue.DCU,
            targetPermissionValue: PermissionValue.VIEW
          }
        }),
        // @anikiforov: TODO: Need translation for `titleTranslation`.
        titleTranslation: 'Devices | DCUs'
      },
      loadComponent: () =>
        import('../pages/equipment/devices/components/device-details/device-details-root/device-details-root.component').then(
          c => c.DeviceDetailsRootComponent
        ),
      providers: [ExportDataService, { provide: EXPORT_URL_TOKEN, useValue: ExportFileUrl.Device }]
    },
    {
      path: PortalPath.metering,
      title: TitleTranslateResolver,
      canMatch: [permissionGuard],
      data: {
        hasListingLayout: true,
        routeItem: new RouteItem({
          hasRight: {
            resourceValue: ResourceValue.METERING,
            targetPermissionValue: PermissionValue.VIEW
          },
          path: `${PortalPath.devices}/${PortalPath.metering}`,
          title: TranslateRegistry.get.MAIN_MENU.TITLE.METERING
        }),
        // @anikiforov: TODO: Need translation for `titleTranslation`.
        titleTranslation: 'Devices | Metering'
      },
      loadComponent: () =>
        import('../pages/equipment/devices/components/metering-listing-page/metering-listing-page.component').then(
          c => c.MeteringListingPageComponent
        ),
      providers: [
        MeteringListingFiltersSourceService,
        devicesMeteringListingModelProvider,
        ListingPageService,
        QueryParamsHandlerService,
        ExportDataService,
        { provide: EXPORT_URL_TOKEN, useValue: ExportFileUrl.Devices },
        provideEffects(DevicesListingPageEffects),
        devicesMapConfigProvider
      ]

      /* @mmezko:
      TODO: Perform refactoring, allowing ListingPageComponent to handle table and map more seamlessly
       Steps:
       1) adapt TabComponent to use RouterLink navigation on tab selection to be able to navigate between the Table and Map routes
       2) Adapt TableComponent: create TableWrapperComponent, that connects to the state and passes all the inputs to the TableComponent;
          also we need to move TableComponent::selectedDeviceId$ field to the PageStateService
       4) Enjoy your PageListing Hex architecture where each Auxiliary route component is modular, portable and interchangeable
       After steps are done:
       1) Remove tab query parameter, as we will have our native navigation through Angular Routing system
         export const routes: PortalRoutes[] = [
           { path: PortalPath.metering, loadChildren: () => import('./listing-page.route.ts').then(mod => mod.LISTING_PAGE_ROUTES) },
         ];
         // move to a separate file ./listing-page.route.ts
         export const LISTING_PAGE_ROUTES: Route[] = [
           {path: 'map', component: MapTileComponent},
           {path: 'table', component: TableWrapperComponent},
         ];
      */
    },
    {
      path: PortalPath.dcu,
      title: TitleTranslateResolver,
      canMatch: [permissionGuard],
      data: {
        hasListingLayout: true,
        routeItem: new RouteItem({
          hasRight: {
            resourceValue: ResourceValue.DCU,
            targetPermissionValue: PermissionValue.VIEW
          },
          path: `${PortalPath.devices}/${PortalPath.dcu}`,
          title: TranslateRegistry.get.MAIN_MENU.TITLE.DCUS_BASE_STATIONS
        }),
        // @anikiforov: TODO: Need translation for `titleTranslation`.
        titleTranslation: 'Devices | DCUs'
      },
      loadComponent: () =>
        import('../pages/equipment/devices/components/dcu-listing-page/dcu-listing-page.component').then(
          c => c.DcuListingPageComponent
        ),
      providers: [
        DcuListingFiltersSourceService,
        devicesDCUListingModelProvider,
        ListingPageService,
        QueryParamsHandlerService,
        ExportDataService,
        { provide: EXPORT_URL_TOKEN, useValue: ExportFileUrl.Devices },
        provideEffects(DevicesListingPageEffects),
        devicesMapConfigProvider
      ]
    },
    {
      path: `${PortalPath.metering}/:id`,
      title: TitleTranslateResolver,
      canMatch: [permissionGuard],
      data: {
        hasDeviceDetailsLayout: true,
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.METERING,
          hasRight: {
            resourceValue: ResourceValue.METERING,
            targetPermissionValue: PermissionValue.VIEW
          }
        }),
        // @anikiforov: TODO: Need translation for `titleTranslation`.
        titleTranslation: 'Devices | Metering'
      },
      loadComponent: () =>
        import('../pages/equipment/devices/components/device-details/device-details-root/device-details-root.component').then(
          c => c.DeviceDetailsRootComponent
        ),
      providers: [ExportDataService, { provide: EXPORT_URL_TOKEN, useValue: ExportFileUrl.Device }]
    },
    {
      path: PortalPath.sensors,
      title: TitleTranslateResolver,
      canMatch: [permissionGuard],
      data: {
        hasListingLayout: true,
        routeItem: new RouteItem({
          hasRight: {
            resourceValue: ResourceValue.SENSORS,
            targetPermissionValue: PermissionValue.VIEW
          },
          path: `${PortalPath.devices}/${PortalPath.sensors}`,
          title: TranslateRegistry.get.MAIN_MENU.TITLE.SENSORS
        }),
        // @anikiforov: TODO: Need translation for `titleTranslation`.
        titleTranslation: 'Devices | Sensors'
      },
      loadComponent: () =>
        import('../pages/equipment/devices/components/sensors-listing-page/sensors-listing-page.component').then(
          c => c.SensorsListingPageComponent
        ),
      providers: [
        SensorsListingFiltersSourceService,
        devicesSensorsListingModelProvider,
        ListingPageService,
        QueryParamsHandlerService,
        ExportDataService,
        { provide: EXPORT_URL_TOKEN, useValue: ExportFileUrl.Devices },
        provideEffects(DevicesListingPageEffects),
        devicesMapConfigProvider
      ]
    },
    {
      path: `${PortalPath.sensors}/:id`,
      canMatch: [permissionGuard],
      title: TitleTranslateResolver,
      data: {
        hasDeviceDetailsLayout: true,
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.SENSORS,
          hasRight: {
            resourceValue: ResourceValue.SENSORS,
            targetPermissionValue: PermissionValue.VIEW
          }
        }),
        // @anikiforov: TODO: Need translation for `titleTranslation`.
        titleTranslation: 'Devices | Sensors'
      },
      loadComponent: () =>
        import('../pages/equipment/devices/components/device-details/device-details-root/device-details-root.component').then(
          c => c.DeviceDetailsRootComponent
        ),
      providers: [ExportDataService, { provide: EXPORT_URL_TOKEN, useValue: ExportFileUrl.Device }]
    }
  ] satisfies PortalRoute['children']
};
